import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import * as ella from "./ellaApi";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem("ella_token") || "");
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem("ella_user") || "null"); } catch { return null; }
  });
  const [loading, setLoading] = useState(false);

  const persist = useCallback((t, u) => {
    if (t) { localStorage.setItem("ella_token", t); setToken(t); }
    if (u) { localStorage.setItem("ella_user", JSON.stringify(u)); setUser(u); }
  }, []);

  const logout = useCallback(async () => {
    try { await ella.doLogout(); } catch {}
    localStorage.removeItem("ella_token");
    localStorage.removeItem("ella_user");
    setToken("");
    setUser(null);
  }, []);

  const login = useCallback(async (email, password) => {
    setLoading(true);
    try {
      const res = await ella.doLogin(email, password);
      if (res.status && res.data?.Token) {
        persist(res.data.Token, { name: res.data.Name, email });
        // res.data also carries TermsRequired / TermsAccepted / Terms —
        // the TermsGate on the home page re-checks via getTermsStatus.
        return { ok: true, data: res.data };
      }
      return { ok: false, message: res.message || "Login gagal" };
    } finally { setLoading(false); }
  }, [persist]);

  const loginWithGoogle = useCallback(async ({ id, name, email, tokenId }) => {
    setLoading(true);
    try {
      const res = await ella.doAuthGoogle({ id, name, email, tokenId });
      if (res.status && res.data?.Token) {
        persist(res.data.Token, { name: res.data.Name || name, email });
        return { ok: true, data: res.data };
      }
      return { ok: false, message: res.message || "Login dengan Google gagal" };
    } finally { setLoading(false); }
  }, [persist]);

  const register = useCallback(async ({ name, email, password }) => {
    setLoading(true);
    try {
      const res = await ella.doRegister({ name, username: email, password, privacyConsent: true });
      if (res.status) {
        // Same as the legacy app: register then login for the session token.
        const r = await ella.doLogin(email, password);
        if (r.status && r.data?.Token) {
          persist(r.data.Token, { name: r.data.Name, email });
          return { ok: true };
        }
        return { ok: true, needsLogin: true };
      }
      return { ok: false, message: res.message || "Pendaftaran gagal" };
    } finally { setLoading(false); }
  }, [persist]);

  const refreshUser = useCallback(async () => {
    if (!localStorage.getItem("ella_token")) return null;
    try {
      const res = await ella.getUser();
      if (res.status && res.data) {
        const u = { name: res.data.Name, email: res.data.Email, phone: res.data.Phone };
        localStorage.setItem("ella_user", JSON.stringify(u));
        setUser(u);
        return u;
      }
    } catch {}
    return null;
  }, []);

  useEffect(() => { if (token) refreshUser(); }, [token, refreshUser]);

  // The API allows one active session per user — logging in on another device
  // revokes this token. ellaApi fires this event when it notices (legacy
  // doHandlerNotAuthorized behaviour).
  useEffect(() => {
    const onRevoked = () => {
      if (!localStorage.getItem("ella_token")) return;
      localStorage.removeItem("ella_token");
      localStorage.removeItem("ella_user");
      setToken("");
      setUser(null);
      toast.info("Sesi kamu berakhir, silakan masuk kembali");
    };
    window.addEventListener("ella:unauthorized", onRevoked);
    return () => window.removeEventListener("ella:unauthorized", onRevoked);
  }, []);

  return (
    <AuthCtx.Provider value={{ token, user, loading, login, loginWithGoogle, register, logout, refreshUser, isAuthed: !!token }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
